import { getLastSegment, getParentPath } from './pathUtils';

// * 파일 전송 메뉴에서 사용

/**
 * 드라이브를 루트 노드로 생성하는 함수
 */
export function createDriveNodes(drives) {
  if (!drives || drives.length === 0) {
    return [];
  }

  return drives.map((drive) => ({
    key: drive.drivePath,
    title: drive.driveName,
    path: drive.drivePath,
    isLeaf: false,
    driveId: drive.driveId,
    driveName: drive.driveName,
    driveType: drive.driveType,
    children: [],
  }));
}

/**
 * 폴더 구조를 순회하며 모든 폴더 노드를 맵에 추가하는 함수
 * @param {Object} folderStructure - 폴더 구조 객체
 * @param {Map} folderMap - 폴더 노드 맵
 */
const collectFolderNodes = (folderStructure, folderMap) => {
  // 모든 디렉토리와 그 하위 폴더들을 맵에 추가
  Object.entries(folderStructure).forEach(([dirPath, folders]) => {
    // 디렉토리 경로가 맵에 없으면 추가
    if (!folderMap.has(dirPath)) {
      folderMap.set(dirPath, {
        key: dirPath,
        title: getLastSegment(dirPath),
        path: dirPath,
        isLeaf: false,
        children: [],
      });
    }

    // 각 디렉토리 내 폴더들도 맵에 추가
    folders.forEach((folder) => {
      if (!folderMap.has(folder.path)) {
        folderMap.set(folder.path, {
          key: folder.path,
          title: folder.title,
          path: folder.path,
          isLeaf: false,
          children: [],
        });
      }
    });
  });
};

/**
 * 폴더 노드들의 부모-자식 관계를 설정하는 함수
 * @param {Map} folderMap - 폴더 노드 맵
 * @param {Array} rootNodes - 루트 노드 배열 (드라이브 노드들)
 */
const buildFolderStructure = (folderMap, rootNodes) => {
  // 루트 경로 맵 생성 (드라이브 경로 -> 드라이브 노드)
  const rootPathMap = new Map();
  rootNodes.forEach((node) => {
    rootPathMap.set(node.path, node);
    // 맵에도 추가
    folderMap.set(node.path, node);
  });

  folderMap.forEach((folder, path) => {
    // 루트 노드는 건너뛰기
    if (rootPathMap.has(path)) return;

    const parentPath = getParentPath(path);
    let parent = folderMap.get(parentPath);

    // 부모가 맵에 없는 경우 부모 노드 생성
    if (!parent) {
      parent = {
        key: parentPath,
        title: getLastSegment(parentPath),
        path: parentPath,
        isLeaf: false,
        children: [],
      };
      folderMap.set(parentPath, parent);
    }

    // 부모가 존재하면 자식으로 추갸
    if (parent && !parent.children.some((child) => child.path === path)) {
      parent.children.push(folder);
    }
  });
};

/**
 * 트리 데이터를 생성하는 함수
 * @param {Object} folderStructure - 폴더 구조 객체
 * @param {Array} drives - 드라이브 목록
 */
export function buildTreeData(folderStructure, drives) {
  // 폴더 구조가 비어있고 드라이브도 없으면 빈 배열 반환
  if (
    (!folderStructure || Object.keys(folderStructure).length === 0) &&
    (!drives || drives.length === 0)
  ) {
    return [];
  }

  // 1. 드라이브를 루트 노드로 생성
  const rootNodes = createDriveNodes(drives);

  // 폴더 구조가 없으면 드라이브 노드만 반환
  if (!folderStructure || Object.keys(folderStructure).length === 0) {
    return rootNodes;
  }

  // 2. 폴더 노드 맵 초기화
  const folderMap = new Map();

  // 3. 모든 폴더 노드 수집
  collectFolderNodes(folderStructure, folderMap);

  // 4. 부모-자식 계층 관계 설정
  buildFolderStructure(folderMap, rootNodes);

  // 5. 최종 트리 데이터 생성
  return rootNodes;
}
